import { parseCsv } from '@avanta/shared/utils/csv';
import { CREATE_EMPLOYEE_STATUS } from 'constants/employees';
import { type CreateEmployeesItem, type CreateEmployeesResult } from 'types/employees';

const EMPLOYEE_CSV_HEADERS = [
  'email',
  'firstName',
  'surname',
  'nif',
  'contactPhone',
  'gender',
  'dayOfBirth',
  'monthOfBirth',
  'yearOfBirth',
  'departmentId',
] as const;

const EMPLOYEE_NICKNAME_MAX_LENGTH = 15;

export type ParseEmployeesCsvContext = {
  enterpriseId: string;
  timeZone: string;
  fallbackDepartmentId?: string;
  generatePassword: () => string;
};

type EmployeeCsvRow = Record<(typeof EMPLOYEE_CSV_HEADERS)[number], string>;

const getNickname = (email: string): string =>
  email.split('@')[0].slice(0, EMPLOYEE_NICKNAME_MAX_LENGTH);

const parseNumberCell = (value: string): number =>
  value.trim() === '' ? Number.NaN : Number(value);

const mapRowToEmployee = (
  row: EmployeeCsvRow,
  context: ParseEmployeesCsvContext
): CreateEmployeesItem => ({
  email: row.email,
  firstName: row.firstName,
  surname: row.surname,
  nif: row.nif,
  contactPhone: row.contactPhone,
  gender: parseNumberCell(row.gender),
  dayOfBirth: parseNumberCell(row.dayOfBirth),
  monthOfBirth: parseNumberCell(row.monthOfBirth),
  yearOfBirth: parseNumberCell(row.yearOfBirth),
  departmentId: row.departmentId || context.fallbackDepartmentId || '',
  password: context.generatePassword(),
  nickname: getNickname(row.email),
  timeZone: context.timeZone,
  enterpriseId: context.enterpriseId,
});

export const getImportIssueLines = (results: CreateEmployeesResult[]): number[] =>
  results.flatMap((result, resultIndex) =>
    result.status === CREATE_EMPLOYEE_STATUS.CREATED ? [] : [resultIndex + 2]
  );

export const parseEmployeesCsv = (
  csvContent: string,
  context: ParseEmployeesCsvContext
): CreateEmployeesItem[] => {
  const rows = parseCsv(csvContent, EMPLOYEE_CSV_HEADERS);
  if (rows.length === 0) {
    throw new Error('CSV must include a header and at least one employee');
  }

  return rows.map(row => mapRowToEmployee(row, context));
};
